import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Dialog } from '@mui/material';

import { MAX_NAME } from '../../../../utils/formValidation/commonConstants';
import { regExps } from '../../../../utils/formValidation/validationPatterns';
import { clearKeys } from '../../../../utils/objectUtils';
import { uuid } from '../../../../utils/stringUtils';
import { hasRole, SUPERUSER } from '../../../../utils/userRoles';
import Button from '../../../formElements/Button';
import RadioButtons from '../../../formElements/RadioButtons';
import SingleSelect from '../../../formElements/SingleSelect';
import TextAreaField from '../../../formElements/TextAreaField';
import TextInputField from '../../../formElements/TextInputField';
import SmartDefs from '../../../ui/notices/smartDefs/SmartDefs';
import { MAX_ACTION_DESCRIPTION, MAX_MODULE_DESCRIPTION } from '../../utils/validationFunctions';

import { validate } from './validateModuleTemplateEditor';

import app from '../../../../app.module.css';
import form from '../../../../commonStyles/formStyles.module.css';
import modal from '../../../../commonStyles/modal.module.css';

const ModuleTemplateEditor = ({
    type,
    entry,
    disabled = false,
    onAdd,
    onUpdate,
    barrierNameDetails,
    isOpen,
    onClose,
    sessionBased = false,
    roles = []
}) => {
    // LOCAL STATE
    const initialEntry = {
        id: `new-${uuid()}`,
        name: '',
        description: '',
        smartTarget: null,
        sessionNumber: '',
        daysAfterPreviousModule: ''
    };
    const [newEntry, setNewEntry] = useState(initialEntry);
    const [errors, setErrors] = useState({});
    const [keys, setKeys] = useState({ smartTargetBarrierNameId: '1' });
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [isSessionFieldsDisabled, setIsSessionFieldsDisabled] = useState(false);
    const [daysAfterDisabled, setDaysAfterDisabled] = useState(false);

    // USE EFFECTS
    useEffect(() => {
        type === 'edit' && setNewEntry(entry);
        setIsSessionFieldsDisabled(!hasRole([SUPERUSER], roles));
    }, []);

    useEffect(() => {
        setDaysAfterDisabled(
            isSessionFieldsDisabled || newEntry.sessionNumber === '' || newEntry.sessionNumber <= 1
        );
    }, [newEntry.sessionNumber, isSessionFieldsDisabled]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    // HELPER FUNCTIONS
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    // EVENT HANDLERS
    const onModuleChange = (key, value, errorKey) => {
        clearError(errorKey);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onSessionNumberChange = (e) => {
        clearError('sessionNumber');
        clearError('daysAfterPreviousModule');
        const value = e.target.value.replace(regExps.DIGITS_R, '');
        setNewEntry((prev) => ({ ...prev, sessionNumber: value, daysAfterPreviousModule: '' }));
    };

    const onDaysAfterPreviousModuleChange = (e) => {
        clearError('daysAfterPreviousModule');
        const value = e.target.value.replace(regExps.DIGITS_R, '');
        setNewEntry((prev) => ({ ...prev, daysAfterPreviousModule: value }));
    };

    const onSmartTargetFlagChange = (option) => {
        setNewEntry((prev) => ({
            ...prev,
            smartTarget: option ? { name: '', barrierNameId: '', action: '' } : null
        }));
        if (!option)
            [
                'smartTargetActionName',
                'smartTargetBarrierNameId',
                'smartTargetActionDescription'
            ].forEach((el) => clearError(el));
    };

    const onSmartTargetChange = (key, value, errorKey) => {
        clearError(errorKey);
        setNewEntry((prev) => ({
            ...prev,
            smartTarget: { ...newEntry.smartTarget, [key]: value }
        }));
    };

    const onCancel = () => {
        setErrors({});
        setKeys(clearKeys(keys));
        onClose();
    };

    const onSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const newErrors = validate(newEntry, type, entry, sessionBased);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        type === 'edit' ? onUpdate(newEntry) : onAdd(newEntry);
    };

    // RENDER
    return (
        <Dialog
            open={isOpen}
            onClose={onCancel}
            aria-labelledby="edit-module-template-modal-title"
            fullWidth>
            <div className={modal.modalHeader} id="edit-module-template-modal-title">
                <h3>{`${type === 'edit' ? 'Edit' : 'Create'} Module Template`}</h3>
            </div>
            <div className={modal.modalContent}>
                <form
                    onSubmit={onSubmit}
                    className={form.formWrapper}
                    data-testid="edit_module_template_form">
                    <TextInputField
                        id={'moduleName'}
                        label={'Module Name'}
                        placeholder={'Enter module name'}
                        mandatory={true}
                        maxLength={MAX_NAME}
                        value={newEntry.name || ''}
                        error={errors.moduleName}
                        onChange={(e) => onModuleChange('name', e.target.value, 'moduleName')}
                    />
                    <TextAreaField
                        id={'moduleDescription'}
                        label={'Module Description'}
                        placeholder={'Enter module description'}
                        mandatory={true}
                        maxLength={MAX_MODULE_DESCRIPTION}
                        value={newEntry.description}
                        count={`${newEntry.description.length || 0}/${MAX_MODULE_DESCRIPTION}`}
                        error={errors.moduleDescription}
                        onChange={(e) =>
                            onModuleChange('description', e.target.value, 'moduleDescription')
                        }
                    />
                    <RadioButtons
                        id={'smartTargetRadio'}
                        label={'Add Smart Target?'}
                        value={!!newEntry.smartTarget}
                        onChange={onSmartTargetFlagChange}
                    />
                    <div hidden={!newEntry.smartTarget}>
                        <h3 className={app.sectionHeading}>Smart Target Details:</h3>
                        <TextInputField
                            id={'smartTargetActionName'}
                            label={'What shall we call this action?'}
                            placeholder={'Enter the name of the action'}
                            mandatory={true}
                            maxLength={MAX_NAME}
                            value={newEntry.smartTarget?.name || ''}
                            error={errors.smartTargetActionName}
                            onChange={(e) =>
                                onSmartTargetChange('name', e.target.value, 'smartTargetActionName')
                            }
                        />
                        <SingleSelect
                            id={'smartTargetBarrierNameId'}
                            key={keys.smartTargetBarrierNameId}
                            label={'Which barrier name aligns to this smart target?'}
                            placeholder={'Select barrier name'}
                            mandatory={true}
                            menuItems={barrierNameDetails || []}
                            selectedId={newEntry.smartTarget?.barrierNameId || ''}
                            selected={
                                barrierNameDetails.find(
                                    (el) => el.id === newEntry.smartTarget?.barrierNameId
                                ) || {}
                            }
                            error={errors.smartTargetBarrierNameId}
                            onChange={(chosenId) =>
                                onSmartTargetChange(
                                    'barrierNameId',
                                    chosenId,
                                    'smartTargetBarrierNameId'
                                )
                            }
                        />
                        <SmartDefs />
                        <TextAreaField
                            id={'smartTargetActionDescription'}
                            label={'Action'}
                            placeholder={'Enter action description'}
                            mandatory={true}
                            maxLength={MAX_ACTION_DESCRIPTION}
                            value={newEntry.smartTarget?.action || ''}
                            count={`${newEntry.smartTarget?.action.length || 0}/${MAX_ACTION_DESCRIPTION}`}
                            error={errors.smartTargetActionDescription}
                            onChange={(e) =>
                                onSmartTargetChange(
                                    'action',
                                    e.target.value,
                                    'smartTargetActionDescription'
                                )
                            }
                        />
                    </div>
                    {sessionBased && (
                        <div data-testid="moduleSessionFields">
                            <TextInputField
                                id={'sessionNumber'}
                                label={'Session Number'}
                                placeholder={'Enter session number'}
                                mandatory={true}
                                disabled={isSessionFieldsDisabled}
                                maxLength={2}
                                value={newEntry.sessionNumber ?? ''}
                                error={errors.sessionNumber}
                                onChange={onSessionNumberChange}
                            />
                            <TextInputField
                                id={'daysAfterPreviousModule'}
                                label={'Days after Previous Module'}
                                placeholder={'Enter days after previous module'}
                                mandatory={!daysAfterDisabled}
                                disabled={daysAfterDisabled}
                                maxLength={2}
                                value={newEntry.daysAfterPreviousModule ?? ''}
                                error={errors.daysAfterPreviousModule}
                                onChange={onDaysAfterPreviousModuleChange}
                            />
                        </div>
                    )}
                    <div className={modal.actionButtons}>
                        <Button
                            id="publish"
                            disabled={disabled || isButtonDisabled}
                            content={type === 'edit' ? 'Update' : 'Create'}
                            customErrorClass="formActionsError"
                            error={errors.button}
                            clearError={() => clearError('button')}
                        />
                        <Button
                            id="editModuleTemplateCancelButton"
                            type="button"
                            content="Cancel"
                            onClick={onCancel}
                        />
                    </div>
                </form>
            </div>
        </Dialog>
    );
};

ModuleTemplateEditor.propTypes = {
    type: PropTypes.string,
    entry: PropTypes.object.isRequired,
    disabled: PropTypes.bool,
    onAdd: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired,
    barrierNameDetails: PropTypes.array.isRequired,
    onClose: PropTypes.func.isRequired,
    isOpen: PropTypes.bool.isRequired,
    sessionBased: PropTypes.bool,
    roles: PropTypes.arrayOf(PropTypes.string)
};
export default ModuleTemplateEditor;
